// The wiring both server-paginated list pages need: the router-backed filter
// dict, the free-text query, the scope, the sort, the page size, and the
// request predicate all of those go into.
//
// One module rather than a copy per page, because the copies drifted and the
// drift was invisible from inside either page. The screens page grew an
// operator guard on its filters that the locations page never received, and
// neither page coerced TanStack's *parsed* search dict back into the strings
// kore's filter grammar reads — so a shared or reloaded link silently dropped
// the screens "Active" filter (see `searchDictAsStrings`). Both were one
// page's bug fixed once; the second copy is what let them ship.
//
// What deliberately stays at the call site is the per-resource translation
// from kore's chip shapes to the wire filters (`screens-filters.ts`,
// `locations-filters.ts`): only the resource knows what its own endpoint accepts.

import type { OrderBy } from "@pej/kore/components";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useCallback, useMemo, useState } from "react";
import type { SearchRq } from "@/api/types";

/**
 * Coerce a TanStack search dict back into the strings kore's filter grammar
 * reads.
 *
 * `useSearch` hands back the *parsed* dict, not the raw query string. A URL
 * parsed from scratch — a bookmark, a shared link, a reload — goes through
 * TanStack's search codec, which turns `?active=true` into the boolean `true`
 * and `?pageSize=25` into the number `25`. kore's `filter-url.ts`
 * `splitValues` accepts strings (and arrays of them) and silently skips
 * everything else, so a `Bool` facet — whose only possible URL values are
 * `true` and `false` — could not survive the trip: no chip in `FILTERED BY`,
 * and an unfiltered request. The screens page's "Active" filter therefore
 * worked when applied and silently disappeared when the resulting link was
 * shared or the page reloaded, which is worse than not working: the table
 * looks answered and is not. A `Number` facet would have been next.
 *
 * Only that direction was broken. A filter committed through the picker
 * navigates with the string kore built, and the router keeps that object as
 * it is until something re-parses the URL — which is why this survived
 * review.
 *
 * Objects and `null` are passed through untouched: `splitValues` skips them
 * either way, and stringifying one would invent a filter value out of
 * `[object Object]`.
 */
export function searchDictAsStrings(search: Record<string, unknown>): Record<string, unknown> {
    const out: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(search)) out[key] = asFilterValue(value);
    return out;
}

function asFilterValue(value: unknown): unknown {
    if (typeof value === "boolean" || typeof value === "number") return String(value);
    if (Array.isArray(value)) return value.map(asFilterValue);
    return value;
}

export interface UsePageSearchOptions<S extends string, B extends string> {
    /**
     * The server's own sort-field names, verbatim. Typed as the wire enum at
     * the call site, so dropping a variant from the enum fails to compile
     * there rather than shipping a sort the endpoint would reject.
     *
     * Pass a module constant: it lands in a `useCallback` dependency list.
     */
    sortFields: readonly S[];
    /** The server's own default order, restated so the first request is explicit. */
    defaultOrder: { field: S; order: "Asc" | "Desc" };
    /** The server's own `searchBy` values. Same contract as `sortFields`. */
    queryFields: readonly B[];
    /** The footer's page sizes. The first is the initial one. */
    pageSizeOptions: readonly number[];
}

export interface PageSearch<S extends string, B extends string> {
    /** The URL's filter dict, in the shape kore's `parseFilters` can read. */
    rawSearch: Record<string, unknown>;
    /**
     * The settled free-text value. kore's `SearchBar` holds the raw keystroke
     * locally and only calls `onQueryChange` once it has settled (200ms), so
     * this is already debounced — there is no second one to add.
     */
    query: string;
    setQuery: (next: string) => void;
    /** `query` trimmed — what actually goes on the wire, and what decides whether an empty table reads "no match" or "none yet". */
    searchText: string;
    searchBy: B | null;
    onSearchByChange: (next: string | null) => void;
    orderBy: { field: S; order: "Asc" | "Desc" };
    onOrderByChange: (next: OrderBy | null) => void;
    onSearchPatch: (patch: Record<string, string | undefined>) => void;
    pageSize: number;
    setPageSize: (next: number) => void;
    /**
     * Everything about the request except the cursor. Hand it to
     * `useCursorStack` as the identity of the request the cursor belongs to,
     * then spread it into the request itself, so the two can never describe
     * different predicates.
     *
     * `pageSize` is part of it, and has to be: not because a stale cursor
     * under a changed page size would be rejected — it wouldn't be
     * (`search_paging::fingerprint` hashes only `filters` and `query`, and
     * `CursorToken` never carries `pageSize`; a keyset anchor is a row
     * position, and continuing from it under a different limit still returns
     * correct rows). It's in here so the pager's page-number arithmetic
     * (`useCursorStack`'s `tokens.length`) still matches what the table is
     * showing once the page boundaries have moved.
     */
    predicate: <F>(filters: F) => Omit<SearchRq<F, S, B>, "pageToken">;
}

export function usePageSearch<S extends string, B extends string>({
    sortFields,
    defaultOrder,
    queryFields,
    pageSizeOptions,
}: UsePageSearchOptions<S, B>): PageSearch<S, B> {
    const navigate = useNavigate();
    // Loose, not the route's own `useSearch()`: the strict slice is whatever
    // the route's (absent) validateSearch declares, which would drop every
    // filter key kore writes. The loose search is the whole URL dict.
    const parsedSearch = useSearch({ strict: false }) as Record<string, unknown>;
    // Memoized on the parsed dict's identity, which TanStack keeps stable
    // across renders of one location: `useKoreSearch` memoizes `parseFilters`
    // — and through it the whole `rq` — on `router.search` by reference, so a
    // fresh object every render would rebuild the request every render.
    const rawSearch = useMemo(() => searchDictAsStrings(parsedSearch), [parsedSearch]);

    const [query, setQuery] = useState("");
    const [searchBy, setSearchBy] = useState<B | null>(null);
    const [orderBy, setOrderBy] = useState<{ field: S; order: "Asc" | "Desc" }>(defaultOrder);
    const [pageSize, setPageSize] = useState(pageSizeOptions[0] ?? 25);

    const onSearchPatch = useCallback(
        (patch: Record<string, string | undefined>) => {
            // kore's patch sets removed filter keys to `undefined`, which is
            // exactly how TanStack Router drops a search param — forward it
            // verbatim. `replace` so a filter tweak isn't a back-button step.
            void navigate({
                to: ".",
                search: (prev: Record<string, unknown>) => ({ ...prev, ...patch }),
                replace: true,
            });
        },
        [navigate],
    );

    const onOrderByChange = useCallback(
        (next: OrderBy | null) => {
            // kore hands back the clicked column's key. The sortable keys are
            // the server's own field names, so an unrecognised one can only
            // mean the manifest and the table disagree — fall back to the
            // default rather than posting a field the server would reject.
            // `null` (the header's third click) lands here too: "unsorted" is
            // not something either endpoint offers, so it means the default.
            setOrderBy(
                next !== null && (sortFields as readonly string[]).includes(next.field)
                    ? { field: next.field as S, order: next.order }
                    : defaultOrder,
            );
        },
        [sortFields, defaultOrder],
    );

    const onSearchByChange = useCallback(
        (next: string | null) => {
            // Mirrors `onOrderByChange`: `SearchModeChip` can only hand back
            // `null` or one of the manifest's own `query.fields` values, so
            // this can't fail today — but it keeps a manifest/wire drift from
            // ever shipping a `searchBy` the endpoint doesn't recognise.
            setSearchBy(next !== null && (queryFields as readonly string[]).includes(next) ? (next as B) : null);
        },
        [queryFields],
    );

    const searchText = query.trim();

    return {
        rawSearch,
        query,
        setQuery,
        searchText,
        searchBy,
        onSearchByChange,
        orderBy,
        onOrderByChange,
        onSearchPatch,
        pageSize,
        setPageSize,
        // Not memoized: its result feeds `useCursorStack` (compared by JSON)
        // and the query key (hashed structurally), so nothing downstream reads
        // the function's identity.
        predicate: <F>(filters: F) => ({
            filters,
            query: searchText ? { value: searchText, searchBy } : null,
            orderBy,
            pageSize,
        }),
    };
}
